import { useState, useEffect } from "react";
import { db, ref, set, onValue, auth } from "../Firebase/firestore";
import { push } from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  Users,
  Plus,
  Phone,
  Mail,
  MapPin,
  Truck,
  Bell,
  Search,
  FileText,
  AlertCircle,
  X,
  Calendar,
  ChevronDown,
} from "lucide-react";
import "./Transportation.css";

const TransportersManagement = () => {
  const [transporters, setTransporters] = useState([]);
  const [clients, setClients] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [selectedTransporter, setSelectedTransporter] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDispatchForm, setShowDispatchForm] = useState(false);
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
  });

  // Add Transporter Form
  const [formData, setFormData] = useState({
    companyName: "",
    gstNumber: "",
    state: "",
    email: "",
    contacts: [{ name: "", phone: "" }],
    bankDetails: {
      accountHolderName: "",
      accountNumber: "",
      ifscCode: "",
      bankName: "",
      branchName: "",
    },
  });

  // Dispatch Form
  const [dispatchData, setDispatchData] = useState({
    routeFrom: "",
    routeTo: "",
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [files, setFiles] = useState({
    bill: null,
    statement: null,
    creditNote: null,
  });
  const [hasIssue, setHasIssue] = useState("no");
  const [issueData, setIssueData] = useState({ issueDate: "", issueNote: "" });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch Transporters
  useEffect(() => {
    const unsubscribe = onValue(ref(db, "transporters"), (snapshot) => {
      if (snapshot.exists()) {
        const list = Object.entries(snapshot.val()).map(([id, d]) => ({
          id,
          ...d,
        }));
        list.sort((a, b) => a.companyName.localeCompare(b.companyName));
        setTransporters(list);
      } else setTransporters([]);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Fetch Clients from profileDashboard
  useEffect(() => {
    const unsubscribe = onValue(ref(db, "profileDashboard"), (snapshot) => {
      if (snapshot.exists()) {
        const list = Object.entries(snapshot.val()).map(([id, d]) => ({
          id,
          company_name: d.company_name || d.username || "Unnamed",
          ...d,
        }));
        setClients(list);
      } else setClients([]);
    });
    return unsubscribe;
  }, []);

  // Fetch Dispatches
  useEffect(() => {
    const dispatchRef = ref(db, "Transport-dispatches");

    const unsubscribe = onValue(dispatchRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();

        let list = [];

        Object.values(data).forEach((clientNode) => {
          Object.entries(clientNode).forEach(([id, d]) => {
            list.push({ id, ...d });
          });
        });

        // 🔥 filter by transporter
        if (selectedTransporter) {
          list = list.filter((d) => d.transporterId === selectedTransporter.id);
        }

        list.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setDispatches(list);
      } else {
        setDispatches([]);
      }
    });

    return unsubscribe;
  }, [selectedTransporter]);

  const filteredClients = clients.filter((c) =>
    (c.company_name || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Add Transporter Handler
  const addTransporter = async (e) => {
    e.preventDefault();
    if (!formData.companyName) {
      alert("Company name is required");
      return;
    }

    try {
      const newRef = push(ref(db, "transporters"));
      await set(newRef, {
        companyName: formData.companyName,
        gstNumber: formData.gstNumber,
        state: formData.state,
        email: formData.email,
        contacts: formData.contacts,
        bankDetails: formData.bankDetails,
        timestamp: new Date().toISOString(),
      });

      setNotification({
        show: true,
        message: "Transporter added successfully!",
      });
      setTimeout(() => setNotification({ show: false, message: "" }), 3000);

      setFormData({
        companyName: "",
        gstNumber: "",
        state: "",
        email: "",
        contacts: [{ name: "", phone: "" }],
        bankDetails: {
          accountHolderName: "",
          accountNumber: "",
          ifscCode: "",
          bankName: "",
          branchName: "",
        },
      });
      setShowAddForm(false);
    } catch (err) {
      console.error(err);
      alert("Error adding transporter");
    }
  };

  const handleDispatchChange = (e) => {
    const { name, value } = e.target;
    setDispatchData((prev) => ({ ...prev, [name]: value }));
  };

  const handleIssueChange = (e) => {
    const { name, value } = e.target;
    setIssueData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (type, file) => {
    if (file && file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }
    setFiles((prev) => ({ ...prev, [type]: file }));
  };
  const handleContactChange = (index, field, value) => {
    const updated = [...formData.contacts];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, contacts: updated }));
  };

  const addContact = () => {
    setFormData((prev) => ({
      ...prev,
      contacts: [...prev.contacts, { name: "", phone: "" }],
    }));
  };

  const handleBankChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      bankDetails: { ...prev.bankDetails, [field]: value },
    }));
  };

  const submitDispatch = async () => {
    if (!selectedClient || !dispatchData.routeFrom || !dispatchData.routeTo) {
      alert("Please fill all required fields");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const storage = getStorage();
    const uploadedUrls = {};

    try {
      const fileTypes = [
        { key: "bill", prefix: "bill" },
        { key: "statement", prefix: "statement" },
      ];
      if (hasIssue === "yes" && files.creditNote) {
        fileTypes.push({ key: "creditNote", prefix: "credit-note" });
      }

      for (const { key, prefix } of fileTypes) {
        const file = files[key];

        if (file && selectedClient?.id) {
          const fileRef = storageRef(
            storage,
            `transporter-docs/${selectedClient.id}/${prefix}-${Date.now()}-${file.name}`,
          );

          await new Promise((resolve, reject) => {
            const task = uploadBytesResumable(fileRef, file);

            task.on(
              "state_changed",
              (snap) => {
                setUploadProgress(
                  Math.round((snap.bytesTransferred / snap.totalBytes) * 100),
                );
              },
              reject,
              async () => {
                uploadedUrls[key] = await getDownloadURL(task.snapshot.ref);
                resolve();
              },
            );
          });
        }
      }

      const dispatchRef = push(
        ref(db, `Transport-dispatches/${selectedClient.id}`),
      );
      await set(dispatchRef, {
        transporterId: selectedTransporter.id,
        transporterName: selectedTransporter.companyName,
        clientId: selectedClient.id,
        clientName: selectedClient.company_name,
        routeFrom: dispatchData.routeFrom,
        routeTo: dispatchData.routeTo,
        hasIssue: hasIssue === "yes",
        issueDate: hasIssue === "yes" ? issueData.issueDate : null,
        issueNote: hasIssue === "yes" ? issueData.issueNote : null,
        files: uploadedUrls,
        timestamp: new Date().toISOString(),
      });

      setNotification({
        show: true,
        message: "Dispatch created successfully!",
      });
      setTimeout(() => setNotification({ show: false, message: "" }), 3000);

      // Reset
      setShowDispatchForm(false);
      setDispatchData({
        routeFrom: "",
        routeTo: "",
      });
      setSelectedClient(null);
      setSearchTerm("");
      setFiles({ bill: null, statement: null, creditNote: null });
      setHasIssue("no");
      setIssueData({ issueDate: "", issueNote: "" });
    } catch (err) {
      console.error(err);
      alert("Error submitting dispatch");
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) return <p className="loading-text">Loading...</p>;

  return (
    <div className="transport-container">
      {notification.show && (
        <div className="notification">
          <Bell size={16} /> {notification.message}
        </div>
      )}

      <div className="layout">
        {/* LEFT SIDEBAR - UNCHANGED */}
        <div className="sidebar">
          <div className="sidebar-header">
            <div className="header-left">
              <Users size={22} />
              <h1>Transporters</h1>
            </div>
            <button
              className="add-btn"
              onClick={() => {
                setSelectedTransporter(null);

                setFormData({
                  companyName: "",
                  gstNumber: "",
                  state: "",
                  email: "",
                  contacts: [{ name: "", phone: "" }],
                  bankDetails: {
                    accountHolderName: "",
                    accountNumber: "",
                    ifscCode: "",
                    bankName: "",
                    branchName: "",
                  },
                });

                setShowAddForm(true);
                setShowDispatchForm(false);
              }}
            >
              <Plus size={18} /> Add
            </button>
          </div>

          <div className="transporters-list">
            {transporters.length === 0 ? (
              <p className="empty-list">No transporters added yet.</p>
            ) : (
              transporters.map((t) => (
                <div
                  key={t.id}
                  className={`transporter-item ${selectedTransporter?.id === t.id ? "active" : ""}`}
                  onClick={() => {
                    setSelectedTransporter(t);
                    setShowAddForm(false);
                    setShowDispatchForm(false);
                  }}
                >
                  <div>
                    <div className="company-name">{t.companyName}</div>
                    <div className="state">{t.state || "No state"}</div>
                  </div>
                  <Truck size={20} color="#0074cc" />
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="right-panel">
          {!selectedTransporter && !showAddForm && !showDispatchForm ? (
            <div className="empty-state">
              <Truck size={80} color="#94a3b8" />
              <h2>Select a transporter</h2>
            </div>
          ) : showAddForm ? (
            /* ==================== ADD TRANSPORTER FORM ==================== */
            <div className="form-container">
              <h2>
                {selectedTransporter
                  ? "Edit Transporter"
                  : "Add New Transporter"}
              </h2>
              <form onSubmit={addTransporter}>
                {/* Company Section */}
                <div className="section-box">
                  <h3>Company Details</h3>

                  <div className="form-group">
                    <label>Company Name *</label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          companyName: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>GST Number</label>
                    <input
                      type="text"
                      value={formData.gstNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, gstNumber: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) =>
                        setFormData({ ...formData, state: e.target.value })
                      }
                      placeholder="Enter state"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="Enter email"
                    />
                  </div>
                </div>

                {/* Bank Section */}
                <div className="section-box">
                  <h3>Bank Details</h3>

                  <div className="form-grid">
                    <input
                      placeholder="Account Holder Name"
                      value={formData.bankDetails.accountHolderName}
                      onChange={(e) =>
                        handleBankChange("accountHolderName", e.target.value)
                      }
                    />
                    <input
                      placeholder="Account Number"
                      value={formData.bankDetails.accountNumber}
                      onChange={(e) =>
                        handleBankChange("accountNumber", e.target.value)
                      }
                    />
                    <input
                      placeholder="IFSC Code"
                      value={formData.bankDetails.ifscCode}
                      onChange={(e) =>
                        handleBankChange("ifscCode", e.target.value)
                      }
                    />
                    <input
                      placeholder="Bank Name"
                      value={formData.bankDetails.bankName}
                      onChange={(e) =>
                        handleBankChange("bankName", e.target.value)
                      }
                    />
                    <input
                      placeholder="Branch Name"
                      value={formData.bankDetails.branchName}
                      onChange={(e) =>
                        handleBankChange("branchName", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Contacts Section */}
                <div className="section-box">
                  <h3>Contact Persons</h3>

                  {formData.contacts.map((c, i) => (
                    <div key={i} className="contact-row">
                      <input
                        placeholder="Contact Name"
                        value={c.name}
                        onChange={(e) =>
                          handleContactChange(i, "name", e.target.value)
                        }
                      />
                      <input
                        placeholder="Phone Number"
                        value={c.phone}
                        onChange={(e) =>
                          handleContactChange(i, "phone", e.target.value)
                        }
                      />
                    </div>
                  ))}

                  <button
                    type="button"
                    className="add-contact-btn"
                    onClick={addContact}
                  >
                    + Add Another Contact
                  </button>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    Add Transporter
                  </button>
                </div>
              </form>
            </div>
          ) : showDispatchForm ? (
            /* ==================== DISPATCH FORM ==================== */
            <div className="dispatch-form-compact">
              <div className="dispatch-header">
                <h3>New Dispatch - {selectedTransporter.companyName}</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowDispatchForm(false)}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="compact-form">
                {/* Select Client Dropdown */}
                <div className="form-group">
                  <label>Select Client</label>
                  <div className="custom-select">
                    <div
                      className="select-input"
                      onClick={() => setShowClientDropdown(!showClientDropdown)}
                    >
                      <span>
                        {selectedClient
                          ? selectedClient.company_name
                          : "Select Client"}
                      </span>
                      <ChevronDown size={18} />
                    </div>

                    {showClientDropdown && (
                      <div className="dropdown-list">
                        <div className="search-wrapper">
                          <Search size={16} />
                          <input
                            type="text"
                            placeholder="Search client..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="client-options">
                          {filteredClients.map((c) => (
                            <div
                              key={c.id}
                              className={`client-option ${selectedClient?.id === c.id ? "selected" : ""}`}
                              onClick={() => {
                                setSelectedClient(c);
                                setShowClientDropdown(false);
                                setSearchTerm("");
                              }}
                            >
                              {c.company_name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Route & Dates */}
                <div className="form-row">
                  <div className="form-group">
                    <label>From</label>
                    <input
                      type="text"
                      name="routeFrom"
                      value={dispatchData.routeFrom}
                      onChange={handleDispatchChange}
                      placeholder="Origin"
                    />
                  </div>
                  <div className="form-group">
                    <label>To</label>
                    <input
                      type="text"
                      name="routeTo"
                      value={dispatchData.routeTo}
                      onChange={handleDispatchChange}
                      placeholder="Destination"
                    />
                  </div>
                </div>

                {/* Documents */}
                <div className="form-group">
                  <label>Documents (Max 5MB)</label>
                  <div className="upload-row">
                    <div className="upload-item">
                      <FileText size={18} /> Bill
                      <input
                        type="file"
                        onChange={(e) =>
                          handleFileChange("bill", e.target.files[0])
                        }
                      />
                    </div>
                    <div className="upload-item">
                      <FileText size={18} /> Statement
                      <input
                        type="file"
                        onChange={(e) =>
                          handleFileChange("statement", e.target.files[0])
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Issue */}
                <div className="form-group">
                  <label>Issue?</label>
                  <div className="radio-inline">
                    <label>
                      <input
                        type="radio"
                        value="no"
                        checked={hasIssue === "no"}
                        onChange={(e) => setHasIssue(e.target.value)}
                      />{" "}
                      No
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="yes"
                        checked={hasIssue === "yes"}
                        onChange={(e) => setHasIssue(e.target.value)}
                      />{" "}
                      Yes
                    </label>
                  </div>

                  {hasIssue === "yes" && (
                    <div className="issue-section">
                      <div className="form-group">
                        <label>Issue Date</label>
                        <input
                          type="date"
                          name="issueDate"
                          value={issueData.issueDate}
                          onChange={handleIssueChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Issue Note</label>
                        <textarea
                          name="issueNote"
                          value={issueData.issueNote}
                          onChange={handleIssueChange}
                          placeholder="Describe issue"
                          rows="2"
                        />
                      </div>
                      <div className="upload-item">
                        <AlertCircle size={18} /> Credit Note
                        <input
                          type="file"
                          onChange={(e) =>
                            handleFileChange("creditNote", e.target.files[0])
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>

                {isUploading && (
                  <div className="progress-container">
                    <div
                      className="progress-bar"
                      style={{ width: `${uploadProgress}%` }}
                    />
                    <span>Uploading... {uploadProgress}%</span>
                  </div>
                )}

                <div className="form-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => setShowDispatchForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="submit-btn"
                    onClick={submitDispatch}
                    disabled={isUploading}
                  >
                    Submit Dispatch
                  </button>
                </div>
              </div>
            </div>
          ) : showDetailsView ? (
            <div className="details-page">
              <div className="details-header">
                <h2>Transporter Details</h2>
                <button onClick={() => setShowDetailsView(false)}>Back</button>
              </div>

              <div className="details-content">
                <h3>Company Details</h3>
                <p>
                  <strong>Company:</strong> {selectedTransporter.companyName}
                </p>
                <p>
                  <strong>GST:</strong> {selectedTransporter.gstNumber}
                </p>
                <p>
                  <strong>State:</strong> {selectedTransporter.state}
                </p>
                <p>
                  <strong>Email:</strong> {selectedTransporter.email || "N/A"}
                </p>

                <h3>Bank Details</h3>
                <p>
                  <strong>Account Holder:</strong>{" "}
                  {selectedTransporter.bankDetails?.accountHolderName}
                </p>
                <p>
                  <strong>Account Number:</strong>{" "}
                  {selectedTransporter.bankDetails?.accountNumber}
                </p>
                <p>
                  <strong>IFSC:</strong>{" "}
                  {selectedTransporter.bankDetails?.ifscCode}
                </p>
                <p>
                  <strong>Bank:</strong>{" "}
                  {selectedTransporter.bankDetails?.bankName}
                </p>
                <p>
                  <strong>Branch:</strong>{" "}
                  {selectedTransporter.bankDetails?.branchName}
                </p>

                <h3>Contacts</h3>
                {selectedTransporter.contacts?.map((c, i) => (
                  <p key={i}>
                    {c.name} - {c.phone}
                  </p>
                ))}
              </div>

              <button
                className="edit-btn"
                onClick={() => {
                  setFormData({
                    companyName: selectedTransporter.companyName || "",
                    gstNumber: selectedTransporter.gstNumber || "",
                    state: selectedTransporter.state || "",
                    email: selectedTransporter.email || "",
                    contacts: selectedTransporter.contacts || [
                      { name: "", phone: "" },
                    ],
                    bankDetails: selectedTransporter.bankDetails || {
                      accountHolderName: "",
                      accountNumber: "",
                      ifscCode: "",
                      bankName: "",
                      branchName: "",
                    },
                  });
                  setShowDetailsView(false);
                  setShowAddForm(true);
                }}
              >
                Edit Details
              </button>
            </div>
          ) : (
            /* MAIN VIEW: Details + Dispatches */
            /* MAIN VIEW: Details + Dispatches */
            <div className="main-content">
              <div className="details-header">
                <div>
                  <h2>{selectedTransporter.companyName}</h2>
                  <div className="info-compact">
                    {selectedTransporter.contacts?.[0] && (
                      <div>
                        <Users size={16} />
                        {selectedTransporter.contacts[0].name} -{" "}
                        {selectedTransporter.contacts[0].phone}
                      </div>
                    )}

                    {selectedTransporter.state && (
                      <div>
                        <MapPin size={16} /> {selectedTransporter.state}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    className="add-dispatch-btn"
                    onClick={() => setShowDispatchForm(true)}
                  >
                    <Plus size={18} /> Add New Dispatch
                  </button>

                  <button
                    className="details-btn"
                    onClick={() => setShowDetailsView(true)}
                  >
                    Transporter Details
                  </button>
                </div>
              </div>

              <div className="dispatches-section">
                <h3 className="section-title">Dispatches</h3>
                {dispatches.length === 0 ? (
                  <p className="no-dispatch">
                    No dispatches yet for this transporter.
                  </p>
                ) : (
                  <div className="dispatches-grid">
                    {dispatches.map((dispatch) => (
                      <div key={dispatch.id} className="dispatch-card">
                        <div className="dispatch-header-card">
                          <span className="client-name">
                            {dispatch.clientName}
                          </span>
                          <span className="dispatch-date">
                            {new Date(dispatch.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="dispatch-route">
                          {dispatch.routeFrom} → {dispatch.routeTo}
                        </div>
                        {dispatch.hasIssue && (
                          <div className="issue-badge">Issue Reported</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransportersManagement;
